import { Contract, ethers, BigNumber } from "ethers";
import * as chains from "./constants/chains";
import COINS from "./constants/coins";
import { primaryProvider, backupProvider, routerAddress, ChainId } from "./constants/chains";
import { useSnackbar } from "notistack";
import numeral from 'numeral';
const ROUTER = require("./build/UniswapV2Router02.json");
const ERC20 = require("./build/ERC20.json");
const FACTORY = require("./build/IUniswapV2Factory.json");
const PAIR = require("./build/IUniswapV2Pair.json");



export function getProvider(useWallet = false) {
  if (useWallet && typeof window.ethereum !== "undefined") {
    return new ethers.providers.Web3Provider(window.ethereum);
  }
  if (primaryProvider) return primaryProvider;
  if (backupProvider) return backupProvider;
  
  throw new Error("No available provider.");
}

function getActiveProvider() {
  if (primaryProvider) return primaryProvider;
  if (backupProvider) return backupProvider;
  
  throw new Error("No available provider.");
}



// Returns network chainId for provided provider
export async function getNetwork(provider = getActiveProvider()) {
  const network = await provider.getNetwork();
  return network.chainId;
}

export function getRouter(address, signer) {
  return new Contract(address, ROUTER.abi, signer);
}

export async function checkNetwork(provider) {
  const chainId = getNetwork(provider);
  if (chains.networks.includes(chainId)){
    return true
  }
  return false;
}

export function getWeth(address, signer) {
  return new Contract(address, ERC20.abi, signer);
}

export function getFactory(address, signer) {
  return new Contract(address, FACTORY.abi, signer);
}

export async function getAccount() {
  const accounts = await window.ethereum.request({
    method: "eth_requestAccounts",
  });

  return accounts[0];
}



//This function checks if a ERC20 token exists for a given address
//    `address` - The Ethereum address to be checked
//    `signer` - The current signer
export function doesTokenExist(address, signer) {
  try {
    return new Contract(address, ERC20.abi, signer);
  } catch (err) {
    return false;
  }
}

export async function getDecimals(token) {
  const decimals = await token.decimals().then((result) => {
      return result;
    }).catch((error) => {
      console.log('No tokenDecimals function for this token, set to 0');
      return 0;
    });
    return decimals;
}

// This function returns an object with 2 fields: `balance` which container's the account's balance in the particular token,
// and `symbol` which is the abbreviation of the token name. To work correctly it must be provided with 4 arguments:
//    `accountAddress` - An Ethereum address of the current user's account
//    `address` - An Ethereum address of the token to check for (either a token or PLS)
//    `provider` - The current provider
//    `signer` - The current signer
// Returns balance and symbol of a token or PLS using active providers

export async function getBalanceAndSymbol(
  accountAddress,
  address,
  provider,
  signer,
  weth_address,
  coins
) {
  try {
    // If no account address is provided, return default coin info
    if (!accountAddress) {
      return {
        balance: "0.00", // Default balance
        symbol: coins[0]?.abbr || "PLS", // Fallback to "PLS"
      };
    }

    if (address === weth_address) {
      // Fetch native PLS balance
      const balanceRaw = await provider.getBalance(accountAddress);
      return {
        balance: ethers.utils.formatEther(balanceRaw),
        symbol: coins[0]?.abbr || "PLS", // Fallback to "PLS" if coins array is undefined or empty
      };
    } else {
      // Fetch ERC20 token balance
      const token = new Contract(address, ERC20.abi, signer);
      const tokenDecimals = await getDecimals(token);
      const balanceRaw = await token.balanceOf(accountAddress);
      
      // Ensure balance is retrieved properly
      if (balanceRaw == null) {
        throw new Error("Unable to retrieve balance from the token contract.");
      }

      const symbol = await token.symbol();
      return {
        balance: parseFloat(ethers.utils.formatUnits(balanceRaw, tokenDecimals)),
        symbol: symbol,
      };
    }
  } catch (error) {
    console.error("The getBalanceAndSymbol function encountered an error!", error);
    return false;
  }
}




// This function swaps two particular tokens / PLS, it can handle switching from PLS to ERC20 token, ERC20 token to PLS, and ERC20 token to ERC20 token.
// No error handling is done, so any issues can be caught with the use of .catch()
// To work correctly, there needs to be 7 arguments:
//    `address1` - An Ethereum address of the token to trade from (either a token or PLS)
//    `address2` - An Ethereum address of the token to trade to (either a token or PLS)
//    `amount` - A float or similar number representing the value of address1's token to trade
//    `routerContract` - The router contract to carry out this trade
//    `accountAddress` - An Ethereum address of the current user's account
//    `signer` - The current signer


export async function swapTokens(
  address1,
  address2,
  amount,
  routerContract,
  accountAddress
) {
  const signer = new ethers.providers.Web3Provider(window.ethereum).getSigner();
  const tokens = [address1, address2];
  const time = Math.floor(Date.now() / 1000) + 200000;
  const deadline = ethers.BigNumber.from(time);

  const token1 = new ethers.Contract(address1, ERC20.abi, signer);
  const token1Decimals = await getDecimals(token1);
  const amountInToken1 = ethers.utils.parseUnits(amount, token1Decimals);

  try {
    const currentAllowanceToken1 = await token1.allowance(accountAddress, routerContract.address);

    // Approve token1 if needed
    if (currentAllowanceToken1.lt(amountInToken1)) {
      console.log('Insufficient allowance for token1. Attempting approval...');
      const approvalTx = await token1.approve(routerContract.address, amountInToken1);

      try {
        await approvalTx.wait();
        console.log(`Approved ${token1.address} transfer for ${amountInToken1.toString()}`);
      } catch (approvalError) {
        console.error('Error during approval transaction:', approvalError.message);
        return { success: false, message: handleApprovalError(approvalError), error: approvalError };
      }
    } else {
      console.log(`Sufficient allowance for ${token1.address} transfer exists.`);
    }

    // Attempt to fetch amounts out
    let amountOut;
    try {
      amountOut = await routerContract.callStatic.getAmountsOut(amountInToken1, tokens);
    } catch (amountOutError) {
      console.error('Error fetching amounts out:', amountOutError.message);
      return { success: false, message: "Failed to retrieve swap amounts", error: amountOutError };
    }

    const wplsAddress = await routerContract.WPLS();
    let tx;

    try {
      console.log(`Executing swap with parameters: ${JSON.stringify({
        address1, address2, amountOut, accountAddress, deadline, amountInToken1
      })}`);

      if (address1 === wplsAddress) {
        tx = await routerContract.swapExactETHForTokens(
          amountOut[1],
          tokens,
          accountAddress,
          deadline,
          { value: amountInToken1 }
        );
      } else if (address2 === wplsAddress) {
        tx = await routerContract.swapExactTokensForETH(
          amountInToken1,
          amountOut[1],
          tokens,
          accountAddress,
          deadline
        );
      } else {
        tx = await routerContract.swapExactTokensForTokens(
          amountInToken1,
          amountOut[1],
          tokens,
          accountAddress,
          deadline
        );
      }

      const receipt = await tx.wait();
      console.log('Transaction successful. Transaction receipt:', receipt);
      return { success: true, message: "Transaction Successful", receipt };

    } catch (swapError) {
      console.error('Error during swap transaction:', swapError.message);
      let errorMessage;
      try {
        errorMessage = handleSwapError(swapError); // Ensure this returns a string
      } catch (e) {
        console.error('Error handling swap error:', e.message);
        errorMessage = "Error handling failed"; // Fallback message
      }
      return { success: false, message: errorMessage, error: swapError };
    }

  } catch (error) {
    console.error('Unexpected error in swapTokens function:', error.message);
    return { success: false, message: error.message || "An unexpected error occurred", error };
  }
}

// Helper function to handle approval errors
function handleApprovalError(error) {
  if (error.code === 4001 || error.message.toLowerCase().includes("user rejected")) {
    return "Approval Rejected by User";
  } else if (error.message.includes("revert")) {
    return "Approval Transaction Reverted";
  }
  return "Approval Failed";
}

// Helper function to handle swap errors
function handleSwapError(error) {
  if (error.code === 4001 || error.message.toLowerCase().includes("user rejected")) {
    return "Transaction Rejected by User";
  } else if (error.message.includes("revert")) {
    return "Transaction Reverted";
  }
  return "Transaction Failed";
}




//This function returns the conversion rate between two token addresses
//    `address1` - An Ethereum address of the token to swaped from (either a token or AUT)
//    `address2` - An Ethereum address of the token to swaped to (either a token or AUT)
//    `amountIn` - Amount of the token at address 1 to be swaped from
//    `routerContract` - The router contract to carry out this swap
export async function getAmountOut(
  address1,
  address2,
  amountIn,
  routerContract,
  signer
) {
  try {
    const token1 = new Contract(address1, ERC20.abi, signer);
    const token1Decimals = await getDecimals(token1);

    const token2 = new Contract(address2, ERC20.abi, signer);
    const token2Decimals = await getDecimals(token2);

    const values_out = await routerContract.getAmountsOut(
      ethers.utils.parseUnits(String(amountIn), token1Decimals),
      [address1, address2]
    );
    const amount_out = values_out[1]*10**(-token2Decimals);
    console.log('amount out: ', amount_out)
    return Number(amount_out);
  } catch {
    return false;
  }
}







export async function fetchReserves(address1, address2, pair, provider) {
  try {
    // Ensure address1, address2, and pair are defined
    if (!address1 || !address2 || !pair) {
      console.log("Invalid addresses or pair. Waiting for both tokens and pair to be defined.");
      return [0, 0];
    }

    const activeProvider = provider || primaryProvider || backupProvider;

    // Create contracts for the tokens
    const coin1 = new Contract(address1, ERC20.abi, activeProvider);
    const coin2 = new Contract(address2, ERC20.abi, activeProvider);

    // Get decimals for each token
    const coin1Decimals = await getDecimals(coin1);
    const coin2Decimals = await getDecimals(coin2);

    // Get reserves from the pair contract
    const reservesRaw = await pair.getReserves();

    // Determine correct order of reserves based on token0/token1 ordering in pair
    const results = [
      (await pair.token0()) === address1 ? reservesRaw[0] : reservesRaw[1],
      (await pair.token1()) === address2 ? reservesRaw[1] : reservesRaw[0],
    ];

    // Scale each reserve to the correct decimal place
    return [
      Number(ethers.utils.formatUnits(results[0], coin1Decimals)),
      Number(ethers.utils.formatUnits(results[1], coin2Decimals)),
    ];
  } catch (err) {
    console.log("Error fetching reserves!");
    console.log(err);
    return [0, 0];
  }
}


// This function returns the reserves stored in a the liquidity pool between the token of address1 and the token
// of address2, as well as the liquidity tokens owned by accountAddress for that pair.
//    `address1` - An Ethereum address of the token to trade from (either a token or AUT)
//    `address2` - An Ethereum address of the token to trade to (either a token or AUT)
//    `factory` - The current factory
//    `signer` - The current signer
export async function getReserves(
  address1,
  address2,
  factory,
  signer,
  accountAddress
) {
  try {
    if (!address1 || !address2) {
      console.log("Address1 or Address2 is undefined. Please check the token addresses.");
      return [0, 0, 0];
    }

    console.log(`Fetching pair address for tokens: ${address1} and ${address2}`);
    const pairAddress = await factory.getPair(address1, address2);

    if (pairAddress === '0x0000000000000000000000000000000000000000') {
      console.log("No reserves yet, pair address is empty.");
      return [0, 0, 0];
    }

    console.log(`Pair address found: ${pairAddress}`);
    const pair = new Contract(pairAddress, PAIR.abi, signer);

    // Fetch raw reserves
    const reservesRaw = await fetchReserves(address1, address2, pair, signer);
    
    // Get user's liquidity token balance in the pair
    const liquidityTokens_BN = await pair.balanceOf(accountAddress);
    const liquidityTokens = Number(
      ethers.utils.formatEther(liquidityTokens_BN)
    );

    return [
      reservesRaw[0].toPrecision(12),
      reservesRaw[1].toPrecision(12),
      liquidityTokens,
    ];
  } catch (err) {
    console.log("Error in getReserves function:", err);
    return [0, 0, 0];
  }
}



